import { Container, Text } from "../uikit";

export interface MenuButtonProps {
  label: string;
  isSelected?: boolean;
  isPressed?: boolean;
  pulse?: number;
  width?: number;
  height?: number;
  fontSize?: number;
}

/**
 * MenuButton component - displays menu item with shadow effect
 *
 * Layer order (back to front):
 * 1. Shadow (darker) - backmost
 * 2. Main text (brighter) - frontmost
 */
export function MenuButton({
  label,
  isSelected = false,
  isPressed = false,
  pulse = 0,
  width = 420,
  height = 72,
  fontSize = 28,
}: MenuButtonProps) {
  const getBgColor = () => {
    if (isPressed) {
      return "rgba(255, 240, 200, 0.3)";
    } else if (isSelected) {
      const bgAlpha = 0.08 + pulse * 0.08;
      return `rgba(255, 200, 100, ${bgAlpha})`;
    }
    return "rgba(100, 140, 180, 0.05)";
  };

  const getBorderColor = () => {
    if (isSelected) {
      return `rgba(255, 220, 100, ${0.6 + pulse * 0.4})`;
    }
    return "rgba(120, 180, 220, 0.3)";
  };

  const getTextColor = () => {
    if (isPressed) return "#ffffff";
    if (isSelected) return "#ffdd70";
    return "#90d0ff";
  };

  const getShadowColor = () => {
    if (isSelected) return "#aa4010";
    return "#1a5080";
  };

  return (
    <Container
      width={width}
      height={height}
      backgroundColor={getBgColor()}
      borderRadius={12}
      borderWidth={isSelected ? 2 : 1}
      borderColor={getBorderColor()}
      alignItems="center"
      justifyContent="center"
    >
      {isSelected && (
        <>
          <Container
            positionType="absolute"
            positionLeft={-45 - 12 - pulse * 6}
            alignItems="center"
            justifyContent="center"
          >
            <Text
              fontSize={36}
              fontWeight="bold"
              color={`rgba(255, 230, 100, ${0.5 + pulse * 0.5})`}
            >
              {">"}
            </Text>
          </Container>
          <Container
            positionType="absolute"
            positionRight={-45 - 12 - pulse * 6}
            alignItems="center"
            justifyContent="center"
          >
            <Text
              fontSize={36}
              fontWeight="bold"
              color={`rgba(255, 230, 100, ${0.5 + pulse * 0.5})`}
            >
              {"<"}
            </Text>
          </Container>
        </>
      )}

      {/* Text with shadow - all absolute for proper z-stacking */}
      <Container positionType="relative">
        {/* Shadow - backmost */}
        {!isPressed && (
          <Text
            positionType="absolute"
            positionLeft={2}
            positionTop={2}
            fontSize={fontSize}
            color={getShadowColor()}
            zIndex={0}
          >
            {label}
          </Text>
        )}
        {/* Main text - frontmost */}
        <Text
          positionType="absolute"
          positionLeft={0}
          positionTop={0}
          fontSize={fontSize}
          color={getTextColor()}
          zIndex={1}
        >
          {label}
        </Text>
        {/* Invisible spacer to maintain layout size */}
        <Text fontSize={fontSize} color="transparent" zIndex={2}>
          {label}
        </Text>
      </Container>
    </Container>
  );
}
